"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useCachedFetch = void 0;
const react_1 = require("react");
const cachedFetchProvider_1 = require("./cachedFetchProvider");
const cachedFetchReducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_INIT':
            return {
                isLoading: true,
                hasError: false,
            };
        case 'FETCH_SUCCESS':
            return {
                isLoading: false,
                hasError: false,
            };
        case 'FETCH_FAILURE':
            return {
                isLoading: false,
                hasError: true,
            };
        default:
            throw new Error();
    }
};
function useCachedFetch(route, options) {
    var _a;
    const { cache, updateCache, globalOptions } = react_1.useContext(cachedFetchProvider_1.CachedFetchContext);
    const memoizedOptions = react_1.useMemo(() => {
        if (!options) {
            return {};
        }
        return options;
    }, [options]);
    const unifiedOptions = react_1.useMemo(() => {
        return Object.assign(Object.assign({}, globalOptions), memoizedOptions);
    }, [globalOptions, memoizedOptions]);
    const [headers] = react_1.useState(unifiedOptions.headers);
    const [shouldRefresh, setShouldRefresh] = react_1.useState(false);
    const [isWaitingForDependencies, setIsWaitingForDependencies] = react_1.useState(true);
    const [state, dispatch] = react_1.useReducer(cachedFetchReducer, {
        isLoading: false,
        hasError: false,
    });
    const fetchCallback = react_1.useCallback(unifiedOptions.fetcher, []);
    react_1.useEffect(() => {
        if (unifiedOptions.dependencies &&
            unifiedOptions.dependencies.some((dependency) => !dependency)) {
            setIsWaitingForDependencies(true);
            return;
        }
        setIsWaitingForDependencies(false);
    }, [unifiedOptions.dependencies]);
    react_1.useEffect(() => {
        if (isWaitingForDependencies) {
            return;
        }
        let mounted = true;
        const fetchData = async () => {
            dispatch({ type: 'FETCH_INIT' });
            try {
                const result = await fetchCallback(route, headers);
                if (mounted) {
                    dispatch({ type: 'FETCH_SUCCESS' });
                }
                updateCache(route, result);
            }
            catch (error) {
                if (mounted) {
                    dispatch({ type: 'FETCH_FAILURE' });
                }
            }
        };
        fetchData();
        return () => {
            mounted = false;
        };
    }, [
        route,
        dispatch,
        updateCache,
        fetchCallback,
        shouldRefresh,
        headers,
        isWaitingForDependencies,
    ]);
    const refresh = react_1.useCallback(() => {
        setShouldRefresh((current) => !current);
    }, []);
    return Object.assign(Object.assign({ data: (_a = cache[route]) !== null && _a !== void 0 ? _a : unifiedOptions.initialValue }, state), { refresh });
}
exports.useCachedFetch = useCachedFetch;
